import { effect, inject, Injectable } from '@angular/core';
import { JAuthService } from '@j/api';
import { BehaviorSubject, combineLatest, filter, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LocalUserConfigService {

  public request: IDBOpenDBRequest = null;
  public db: IDBDatabase = null;
  private version = 1;
  private ready = new BehaviorSubject<boolean>(false);
  private knrbenutzer = new BehaviorSubject<number>(null);
  private tableName = "appUserConfigKeyValue";
  private memoryStore: Record<string, unknown> = {};
  private useMemoryFallback = false;

  constructor() {
    const auth = inject(JAuthService);
    effect(() => {
      const userId = auth.loginInfo()?.mandant.knrbenutzer;
      if (userId) {
        this.knrbenutzer.next(userId);
      }
    });

    this.openDb();
  }

  private openDb() {
    if (window.indexedDB == undefined) {
      this.useMemoryFallback = true;
      this.ready.next(true);
      return;
    }

    this.request = window.indexedDB.open("AppUserConfig", this.version);

    this.request.onupgradeneeded = () => {
      console.log('[DB_USER_CONFIG]::UPGRADE NEEDED');
      this.db = this.request.result;
      this.upgrade();
    };

    this.request.onsuccess = () => {
      console.log('[DB_USER_CONFIG]::OPENED');
      this.db = this.request.result;
      this.db.onclose = () => {
        console.warn('[DB_USER_CONFIG]::CLOSED – reopening');
        this.db = null;
        this.openDb();
      };
      this.ready.next(true);
    };

    this.request.onerror = ev => {
      console.error('[DB_USER_CONFIG]::ERROR', ev);
      this.useMemoryFallback = true;
      this.ready.next(true);
    };

    this.request.onblocked = () => {
      console.warn('[DB_USER_CONFIG]::BLOCKED');
    };
  }

  private upgrade() {
    if (!this.db.objectStoreNames.contains(this.tableName)) {
      this.db.createObjectStore(this.tableName, { keyPath: "key", autoIncrement: false });
    }
  }

  async waitUntilReady(): Promise<void> {
    await firstValueFrom(
      combineLatest([
        this.ready.pipe(filter(v => v === true)),
        this.knrbenutzer.pipe(filter(e => !!e))
      ])
    );
  }

  private storageKey(key: string): string {
    return key + '_' + this.knrbenutzer.value;
  }

  public save(key: string, value: any): Promise<boolean> {
    return new Promise<boolean>(async (res, rej) => {
      await this.waitUntilReady();
      this._save(key, value, () => { res(true); }, () => { rej(false); });
    });
  }

  private _save(key: string, value: any, success: Function = () => {}, error: Function = () => {}) {
    const fullKey = this.storageKey(key);
    this.memoryStore[fullKey] = value;

    if (this.useMemoryFallback || !this.db) {
      success();
      return;
    }

    const transaction = this.db.transaction(this.tableName, 'readwrite');
    const store = transaction.objectStore(this.tableName);
    const query = store.put({ key: fullKey, value: value });

    query.onerror = ev => {
      console.error('[DbUserConfigService::_save]', this.tableName, (ev.target as IDBRequest).error);
      error(ev.target);
    };
    transaction.oncomplete = () => {
      success();
    };
    transaction.onerror = () => {
      error(transaction.error);
    };
  }

  public load(key: string): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      await this.waitUntilReady();
      const fullKey = this.storageKey(key);

      if (this.memoryStore[fullKey] !== undefined) {
        resolve(this.memoryStore[fullKey]);
        return;
      }

      if (this.useMemoryFallback || !this.db) {
        resolve(null);
        return;
      }

      const tx = this.db.transaction(this.tableName, 'readonly');
      const store = tx.objectStore(this.tableName);
      const query = store.get(fullKey);

      query.onerror = () => {
        reject(query.error);
      };

      query.onsuccess = () => {
        const value = query.result?.value;
        if (value !== undefined) {
          this.memoryStore[fullKey] = value;
        }
        resolve(value === undefined ? null : value);
      };

      tx.onabort = () => {
        reject(tx.error);
      };
    });
  }
}
